// borrowed from https://www.codemzy.com/blog/fix-chunkloaderror-react

// A failed dynamic import after a deploy looks like one of these. We only want
// to force a reload for these (stale/missing chunk) cases — not for arbitrary
// errors thrown while evaluating an otherwise-loaded module, which a reload
// can't fix and would just loop on.
const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;
  const name = (error as { name?: string }).name || "";
  const message = (error as { message?: string }).message || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk \d+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) || // Safari
    /'text\/html'/.test(message) // server returned index.html for a missing chunk
  );
};

// a function to retry loading a chunk to avoid chunk load error for out of date code
export const lazyRetry = function <T>(
  componentImport: () => Promise<T>,
  name: string
) {
  return new Promise<T>((resolve, reject) => {
    const key = `retry-${name}-refreshed`;
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem(key) || "false"
    );
    // try to import the component
    componentImport()
      .then((component) => {
        window.sessionStorage.setItem(key, "false"); // success so reset the refresh
        resolve(component);
      })
      .catch((error) => {
        if (!hasRefreshed && isChunkLoadError(error)) {
          // Stale/missing chunk (typically a deploy happened while the tab was
          // open) and we haven't refreshed yet — reload once to pick up the new
          // build. Logged so the failing chunk is visible in the console.
          // eslint-disable-next-line no-console
          console.warn(`lazyRetry: reloading after failed import of "${name}"`, error);
          window.sessionStorage.setItem(key, "true"); // we are now going to refresh
          return window.location.reload(); // refresh the page
        }
        // eslint-disable-next-line no-console
        console.error(`lazyRetry: import of "${name}" failed`, error);
        reject(error); // Default error behaviour (already refreshed, or not a chunk error)
      });
  });
};
